import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  Copy,
  Plug,
  RefreshCw,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { env } from "@/lib/env";
import { isExternalBookingSystem } from "@/components/dashboard/ExternalSystemNotice";

interface VenueRow {
  id: string;
  venue_name: string;
  external_sync_token: string | null;
  booking_system: string | null;
  mindbody_connected_at: string | null;
}

const ENDPOINT = `${env.supabaseUrl}/functions/v1/external-slot-sync`;

/**
 * Venue → Integrations.
 *
 * Surface for venues whose existing booking system can fire webhooks
 * (Bookteq, Skedda Plus, custom) to keep their availability in sync
 * with Whistll. Shows the endpoint URL, their per-venue token, an
 * example payload they can hand to their booking-system admin, and
 * controls to rotate the token or wipe their externally-synced
 * slots (used when switching booking systems).
 *
 * What we deliberately don't do here:
 *   * outbound integrations (we don't poll/push to anyone - we receive)
 *   * provider-specific adapters (every system's payload differs; we
 *     accept the shape documented below and let the venue's side do
 *     the mapping)
 */
export function VenueIntegrationsTab() {
  const { profile } = useAuth();
  const [venue, setVenue] = useState<VenueRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [externalSlotCount, setExternalSlotCount] = useState<number | null>(
    null,
  );
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState<"rotate" | "wipe" | null>(null);

  async function load() {
    if (!profile?.venue_application_id) return;
    const [{ data: v }, { count }] = await Promise.all([
      supabase
        .from("venue_applications")
        .select(
          "id, venue_name, external_sync_token, booking_system, mindbody_connected_at",
        )
        .eq("id", profile.venue_application_id)
        .single(),
      // We can't filter availability_slots directly on
      // venue_application_id (the FK is via courts), so go through
      // courts: count the venue's courts' external slots.
      supabase
        .from("availability_slots")
        .select("id, courts!inner(venue_application_id)", {
          count: "exact",
          head: true,
        })
        .eq("courts.venue_application_id", profile.venue_application_id)
        .eq("status", "external"),
    ]);
    setVenue((v ?? null) as VenueRow | null);
    setExternalSlotCount(count ?? 0);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.venue_application_id]);

  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy. Select the text manually.");
    }
  }

  async function rotateToken() {
    if (!venue) return;
    if (
      !window.confirm(
        "Rotate the token? Any system currently pushing slots will start failing until you give it the new value.",
      )
    ) {
      return;
    }
    setBusy("rotate");
    // gen_random_uuid() lives on the venue row; easiest path is to
    // ask Postgres to regenerate it via a DEFAULT trick. We just
    // null it out and let the column default kick back in.
    const { error: clear } = await supabase
      .from("venue_applications")
      .update({ external_sync_token: null })
      .eq("id", venue.id);
    if (clear) {
      setBusy(null);
      return toast.error(clear.message);
    }
    // Setting to null doesn't run the default; we need to actually
    // produce a new uuid client-side.
    const fresh = crypto.randomUUID();
    const { error } = await supabase
      .from("venue_applications")
      .update({ external_sync_token: fresh })
      .eq("id", venue.id);
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success("Token rotated. Update your booking system with the new value.");
    setRevealed(true);
    await load();
  }

  async function wipeExternalSlots() {
    if (!venue) return;
    if (
      !window.confirm(
        `Delete all ${externalSlotCount ?? "external"} externally-synced slots? Use this when you're switching booking systems. Slots you've manually marked open in Whistll are NOT affected.`,
      )
    ) {
      return;
    }
    setBusy("wipe");
    // Filter via the FK chain courts -> venue_application_id. The
    // courts!inner trick we used for the count returns rows; the
    // PostgREST DELETE doesn't take a join, so we fetch the ids and
    // delete by id.
    const { data: ids, error: selErr } = await supabase
      .from("availability_slots")
      .select("id, courts!inner(venue_application_id)")
      .eq("courts.venue_application_id", venue.id)
      .eq("status", "external");
    if (selErr) {
      setBusy(null);
      return toast.error(selErr.message);
    }
    const idList = (ids ?? []).map((r: { id: string }) => r.id);
    if (idList.length === 0) {
      setBusy(null);
      return toast("No external slots to delete.");
    }
    const { error } = await supabase
      .from("availability_slots")
      .delete()
      .in("id", idList);
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success(`Deleted ${idList.length} external slots.`);
    await load();
  }

  if (loading) {
    return <PageLoader />;
  }
  if (!venue) {
    return (
      <p className="text-muted-foreground">
        Couldn't load your venue record. Email{" "}
        <a
          href="mailto:hello@whistll.com"
          className="underline underline-offset-2"
        >
          hello@whistll.com
        </a>
        .
      </p>
    );
  }

  const tokenDisplay = revealed
    ? venue.external_sync_token ?? "-"
    : "•".repeat(36);

  const samplePayload = JSON.stringify(
    {
      venue_application_id: venue.id,
      slots: [
        {
          court_id: "<one of your court ids>",
          external_source: "your-system-name",
          external_ref: "unique-id-from-your-system",
          starts_at: "2026-05-01T18:00:00Z",
          ends_at: "2026-05-01T19:00:00Z",
          status: "external",
          price_minor: 500,
        },
      ],
    },
    null,
    2,
  );

  const isExternal = isExternalBookingSystem(venue.booking_system);
  const systemName = (venue.booking_system ?? "").trim();
  const usesMindbody = systemName.toUpperCase() === "MINDBODY";

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Venue partner · Integrations
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Booking system integration
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Already use a booking system? Point its webhook at the URL below
          and Whistll will keep your availability in sync. Whoever runs
          your existing system needs the URL, your per-venue token, and
          the payload format.
        </p>
      </header>

      {/* Direct-sync banner - shown for every Tier 1 venue (those who
          applied with an external booking system like MINDBODY, Club
          Automation, Bookteq, etc.). The connect flow itself is
          stubbed; this block makes the placeholder visible so the
          venue knows direct sync is coming and they don't have to
          webhook-bridge it themselves. We single out the MINDBODY
          flow with the Activation Code wording since that's how their
          API works; other systems get a more general message until we
          wire each one specifically. */}
      {isExternal && (
        <section className="mb-6 rounded-2xl border border-dashed border-volt/40 bg-volt-subtle p-6">
          <div className="mb-3 flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-volt/15 text-volt">
              <Plug className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-wider text-volt">
                {systemName} · direct sync
              </p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight">
                {venue.mindbody_connected_at && usesMindbody
                  ? `Connected to ${systemName}`
                  : `Direct ${systemName} integration is on the way`}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {venue.mindbody_connected_at && usesMindbody ? (
                  <>
                    Your availability is syncing automatically with{" "}
                    <span className="text-foreground">{venue.venue_name}</span>'s
                    {" "}{systemName} site. Connected on{" "}
                    {new Date(venue.mindbody_connected_at).toLocaleDateString()}.
                  </>
                ) : usesMindbody ? (
                  <>
                    You marked MINDBODY as your booking system. Direct
                    sync rolls out ahead of launch - once it's ready
                    you'll connect{" "}
                    <span className="text-foreground">{venue.venue_name}</span>{" "}
                    here in two minutes (paste an Activation Code from
                    your MINDBODY admin panel) and your courts +
                    availability will flow automatically. Until then,
                    nothing for you to do here.
                  </>
                ) : (
                  <>
                    You marked {systemName} as your booking system.
                    Direct sync rolls out ahead of launch - once it's
                    ready for{" "}
                    <span className="text-foreground">{venue.venue_name}</span>,{" "}
                    you'll connect here in a one-time ~2-min setup and
                    your courts + availability will flow automatically.
                    Until then, nothing for you to do - manage your
                    schedule in {systemName} as you always have.
                  </>
                )}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" disabled>
                  <Plug className="h-3.5 w-3.5" />
                  {venue.mindbody_connected_at && usesMindbody
                    ? "Manage connection"
                    : `Connect ${systemName} (coming soon)`}
                </Button>
                <span className="text-xs text-muted-foreground">
                  ETA: ahead of launch - we'll let you know.
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Endpoint card */}
      <section className="mb-6 rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <Plug className="h-4 w-4 text-volt" />
          <p className="text-sm font-bold uppercase tracking-wider">
            Webhook endpoint
          </p>
        </div>
        <Field
          label="POST URL"
          value={ENDPOINT}
          onCopy={() => copy(ENDPOINT, "URL")}
        />
        <Field
          label="Header"
          value={`x-venue-token: ${tokenDisplay}`}
          onCopy={() =>
            venue.external_sync_token
              ? copy(venue.external_sync_token, "Token")
              : toast.error("Token isn't set yet - rotate to generate one.")
          }
          rightSlot={
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              {revealed ? "Hide" : "Reveal"}
            </button>
          }
        />
        <p className="mt-3 text-xs text-muted-foreground">
          Treat the token like a password. Rotate it any time the value
          gets exposed (left in a screenshot, in a former employee's
          inbox, etc).
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={rotateToken}
            disabled={busy !== null}
          >
            <RefreshCw
              className={busy === "rotate" ? "h-4 w-4 animate-spin" : "h-4 w-4"}
            />
            Rotate token
          </Button>
        </div>
      </section>

      {/* Sample payload */}
      <section className="mb-6 rounded-2xl border border-border bg-card p-6">
        <p className="mb-3 text-sm font-bold uppercase tracking-wider">
          Sample payload
        </p>
        <p className="mb-3 text-sm text-muted-foreground">
          Send a JSON body shaped like this. Each call is an upsert by{" "}
          <code className="rounded bg-secondary px-1.5 py-0.5 text-xs">
            (court_id, external_source, external_ref)
          </code>
          , so re-pushing the same row updates rather than duplicates.
        </p>
        <div className="relative">
          <pre className="overflow-auto rounded-md border border-border bg-background p-4 text-xs leading-relaxed">
            {samplePayload}
          </pre>
          <button
            type="button"
            onClick={() => copy(samplePayload, "Sample")}
            className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] font-semibold hover:bg-secondary"
          >
            <Copy className="h-3 w-3" />
            Copy
          </button>
        </div>
        <ul className="mt-4 space-y-1 text-xs text-muted-foreground">
          <li>
            <strong>status:</strong>{" "}
            <code className="rounded bg-secondary px-1 py-0.5 text-[11px]">
              external
            </code>{" "}
            (default - visible to your dashboard, not bookable on Whistll)
            or{" "}
            <code className="rounded bg-secondary px-1 py-0.5 text-[11px]">
              open
            </code>{" "}
            (bookable by Whistll players).
          </li>
          <li>
            <strong>price_minor:</strong> per-player price in your
            country's smallest unit (pence, cents). Optional - falls
            back to the court's base price.
          </li>
          <li>
            <strong>court_id:</strong> one of your published courts. Get
            these by selecting a court from the Courts tab and copying
            its id from the URL.
          </li>
        </ul>
      </section>

      {/* Switching systems */}
      <section className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <p className="text-sm font-bold uppercase tracking-wider">
            Switching booking systems
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          About to migrate from one booking system to another? Wipe your
          existing externally-synced slots first so the new system has a
          clean canvas to push into. Slots you've created manually in
          Whistll are <strong className="text-foreground">not</strong>{" "}
          affected.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={wipeExternalSlots}
            disabled={busy !== null || externalSlotCount === 0}
          >
            <Trash2
              className={busy === "wipe" ? "h-4 w-4 animate-spin" : "h-4 w-4"}
            />
            Delete all external slots
          </Button>
          <span className="text-xs text-muted-foreground">
            {externalSlotCount === null
              ? "-"
              : `${externalSlotCount} externally-synced slot${externalSlotCount === 1 ? "" : "s"} on file`}
          </span>
        </div>
      </section>

      <p className="mt-8 text-xs text-muted-foreground">
        Stuck connecting? Email{" "}
        <a
          href="mailto:info@whistll.com?subject=Integration%20help"
          className="font-semibold text-foreground underline underline-offset-2 hover:text-volt"
        >
          info@whistll.com
        </a>{" "}
        with your booking system name and we'll write up the exact
        webhook config you need.
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  onCopy,
  rightSlot,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  rightSlot?: React.ReactNode;
}) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground">
          {label}
        </span>
        {rightSlot}
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded-md border border-border bg-background px-3 py-2 font-mono text-xs">
          {value}
        </code>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-card hover:bg-secondary"
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// Suppress unused-import warning when we drop the Check icon usage
// (kept around for future "verified webhook" indicator).
void Check;
